import {WorkTermsSummary} from '@/components/JobCostPlanning';
import { ContractorCalendar } from '@/components/ContractorCalendar';
import { money } from '@/lib/budget';
/**
 * CP-14.3 — Crew Shape Search (capability + capacity matcher).
 *
 * Capability-first, capacity-first discovery. The client describes the crew
 * they need (multiple resource types × qty × site-lead requirements), an
 * optional Phase-12 track, region, and verification toggle, and the matcher
 * returns owners (companies + solo contractors) whose `workforce_offering`
 * rows satisfy the shape.
 *
 * Lock §51 honored: no staff-count multiplier; cards do NOT show "size" as a
 * ranking signal — only as an informational employee_count_band pill.
 * Keyword search is intentionally a secondary, de-emphasized control.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";
import { PreferredButton, usePreferred, preferredFirst } from "@/components/PreferredContractors";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { jobRequestUrl } from "@/lib/jobRequests";

function Pill({ tone = "neutral", children, testid }) {
  const tones = {
    neutral: "bg-slate-100 text-slate-700 border-slate-200",
    ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
    warn: "bg-amber-50 text-amber-700 border-amber-200",
    info: "bg-sky-50 text-sky-700 border-sky-200",
    danger: "bg-rose-50 text-rose-700 border-rose-200",
    company: "bg-indigo-50 text-indigo-700 border-indigo-200",
    self: "bg-slate-50 text-slate-600 border-slate-200",
  };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${tones[tone] || tones.neutral}`}
    >
      {children}
    </span>
  );
}

const EVIDENCE_PILL = {
  company_verified: { tone: "company", label: "Company-verified" },
  client_verified: { tone: "info", label: "Client-verified" },
  self: { tone: "self", label: "Self-declared" },
};

function emptyReq() {
  return { title_code: "", quantity: 1, site_lead_capable: false };
}

export default function CrewShapeSearch({ marketplace = null, headerTitle = null, headerDescription = null, embedded = false, jobId, projectRegion, projectHelpers = 0, planning, onMatches }) {
  const Shell = embedded ? React.Fragment : Layout;
  const Heading = embedded ? "h3" : "h1";
  const preferred = usePreferred();
  const { user } = useAuth();
  const requestNumber = useRef(0);
  const initialSearch=useRef(false);
  const [nextOffset, setNextOffset] = useState(null);
  const [total, setTotal] = useState(0);
  const [applied, setApplied] = useState(null);
  const [titles, setTitles] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [regions, setRegions] = useState([]);

  const [requirements, setRequirements] = useState([
    { title_code: "lead_tech", quantity: 1, site_lead_capable: true },
    ...(Number(projectHelpers)>0 ? [{ title_code: "helper_apprentice", quantity: Number(projectHelpers), site_lead_capable: false }] : []),
  ]);
  const [trackSlug, setTrackSlug] = useState("");
  const [regionCode, setRegionCode] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [includeSolo, setIncludeSolo] = useState(true);
  const [includeCompanies, setIncludeCompanies] = useState(true);
  const [keyword, setKeyword] = useState("");

  const [busy, setBusy] = useState(false);
  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState([]);
  const [priceOrder,setPriceOrder]=useState('default');
  const [catalogError, setCatalogError] = useState('');
  useEffect(() => { if (projectRegion) setRegionCode(projectRegion); }, [projectRegion]);
  useEffect(() => {
    (async () => {
      try {
        const [tax, tk, rg] = await Promise.all([
          api("/title-taxonomy"),
          api("/catalogs/tracks"),
          api("/catalog/regions"),
        ]);
        setTitles(tax.items || []);
        setTracks(tk.data || []);
        setRegions(rg.data || []);
      } catch (e) {
        setCatalogError(`Could not load search filters: ${e.message}`);
      }
    })();
  }, []);

  useEffect(()=>{onMatches?.(results);},[results,onMatches]);

  const usedCodes = useMemo(
    () => new Set(requirements.map((r) => r.title_code).filter(Boolean)),
    [requirements],
  );

  const updateReq = (idx, patch) =>
    setRequirements((p) => p.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addReq = () => setRequirements((p) => [...p, emptyReq()]);
  const removeReq = (idx) =>
    setRequirements((p) => (p.length > 1 ? p.filter((_, i) => i !== idx) : p));

  const submit = async (more = false) => {
    const reqs = requirements.filter((r) => r.title_code && Number(r.quantity) > 0);
    if (reqs.length === 0) {
      toast.error("Choose at least one role for your crew.");
      return;
    }
    if (new Set(reqs.map((r) => r.title_code)).size !== reqs.length) {
      toast.error("Choose each role only once.");
      return;
    }
    const requestId = ++requestNumber.current;
    setBusy(true);
    try {
      const body = more && applied ? { ...applied, offset: nextOffset } : {
          requirements: reqs.map((r) => ({
            title_code: r.title_code,
            quantity: Number(r.quantity) || 1,
            site_lead_capable: !!r.site_lead_capable,
          })),
          track_slug: trackSlug || null,
          region_code: regionCode || null,
          verified_only: !!verifiedOnly,
          include_solo: !!includeSolo,
          include_companies: !!includeCompanies,
          keyword: keyword.trim() || null,
          marketplace: marketplace || null,
          ...(planning?.hours>0 ? {quote_hours:planning.hours,quote_currency:planning.currency,...(planning.regionId?{quote_region_id:planning.regionId}:{})} : {}),
          price_order:priceOrder,
          limit: 25,
          offset: 0,
        };
      const r = await api("/marketplace/crew-shape-search", { method: "POST", body });
      if (requestId !== requestNumber.current) return;
      setResults(prev => more ? [...prev, ...(r.items || [])] : (r.items || []));
      setNextOffset(r.next_offset ?? null); setTotal(r.total ?? r.count ?? 0);
      if (!more) setApplied(body);
      setSearched(true);
    } catch (e) {
      toast.error(e.message);
    } finally {
      if (requestId === requestNumber.current) setBusy(false);
    }
  };

  useEffect(() => {
    if (!searched) return;
    const timer = setTimeout(() => submit(), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackSlug, regionCode, verifiedOnly, includeSolo, includeCompanies, keyword, requirements, planning?.hours, planning?.currency, planning?.regionId, priceOrder]);

  useEffect(()=>{if(embedded&&titles.length&&!initialSearch.current){initialSearch.current=true;submit();}},[embedded,titles]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Shell>
      <div className="max-w-6xl mx-auto" data-testid={TID.cssRoot}>
        <header className="mb-6">
          <Heading className="text-2xl font-semibold tracking-tight" data-testid="css-header-title">
            {headerTitle || "Find a company or crew"}
          </Heading>
          <p className="text-sm text-[var(--avcp-ink-soft)] mt-1">
            {headerDescription ||
              "Choose the roles and number of people you need. Choose your contractor and send the selected project as a job request."}
          </p>
        </header>

        {/* Crew shape builder */}
        <section className="avcp-card p-6 mb-6">
          <h2 className="text-lg font-semibold mb-3">What crew do you need?</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-[var(--avcp-ink-soft)] border-b border-[var(--avcp-line)]">
                  <th className="py-2 pr-3">Role</th>
                  <th className="py-2 pr-3 w-20">Qty</th>
                  <th className="py-2 pr-3 w-44">Requires site lead?</th>
                  <th className="py-2 pr-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {requirements.map((r, idx) => (
                  <tr key={idx}
                      className="border-b border-[var(--avcp-line)] align-top"
                      data-testid={`${TID.cssReqRow}-${idx}`}>
                    <td className="py-2 pr-3">
                      <select
                        className="avcp-input"
                        data-testid={`${TID.cssReqTitle}-${idx}`}
                        value={r.title_code}
                        onChange={(e) => updateReq(idx, { title_code: e.target.value })}
                      >
                        <option value="">— Select —</option>
                        {titles.map((t) => {
                          const disabled = t.code !== r.title_code && usedCodes.has(t.code);
                          return (
                            <option key={t.code} value={t.code} disabled={disabled}>
                              {t.display_label_default}
                              {disabled ? " (used above)" : ""}
                            </option>
                          );
                        })}
                      </select>
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="number"
                        min="1"
                        max="999"
                        className="avcp-input"
                        data-testid={`${TID.cssReqQty}-${idx}`}
                        value={r.quantity}
                        onChange={(e) => updateReq(idx, { quantity: e.target.value })}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <label className="inline-flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          data-testid={`${TID.cssReqSiteLead}-${idx}`}
                          checked={!!r.site_lead_capable}
                          onChange={(e) =>
                            updateReq(idx, { site_lead_capable: e.target.checked })
                          }
                        />
                        <span title="Only match rows that can run the job on site without a client-supplied lead">
                          Site lead capable
                        </span>
                      </label>
                    </td>
                    <td className="py-2 pr-3">
                      {requirements.length > 1 && (
                        <button
                          type="button"
                          className="text-[var(--avcp-ink-soft)] hover:text-red-600"
                          data-testid={`${TID.cssReqRemove}-${idx}`}
                          title="Remove row"
                          onClick={() => removeReq(idx)}
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="avcp-btn mt-3"
                  data-testid={TID.cssReqAdd} onClick={addReq}>
            + Add another role
          </button>
        </section>

        {/* Filters */}
        <section className="avcp-card p-6 mb-6">
          <h2 className="text-lg font-semibold mb-3">Refine</h2>
          {catalogError && <p role="alert" className="text-rose-700 mb-3">{catalogError}</p>}
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-medium block mb-1">Specialty</span>
              <select
                className="avcp-input"
                data-testid={TID.cssTrack}
                value={trackSlug}
                onChange={(e) => setTrackSlug(e.target.value)}
              >
                <option value="">Any specialty</option>
                {tracks.map((t) => (
                  <option key={t.slug || t.code} value={t.slug || t.code}>
                    {t.display_name || t.label || t.slug}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-medium block mb-1">Service region</span>
              <select
                className="avcp-input"
                data-testid={TID.cssRegion}
                value={regionCode}
                onChange={(e) => setRegionCode(e.target.value)}
              >
                <option value="">Any region</option>
                {regions.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label || r.code}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-4 mt-4">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid={TID.cssVerifiedOnly}
                checked={verifiedOnly}
                onChange={(e) => setVerifiedOnly(e.target.checked)}
              />
              <span>Verified capability only</span>
              <span className="text-xs text-[var(--avcp-ink-soft)]">
                (require verified qualifications)
              </span>
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid={TID.cssIncludeCompanies}
                checked={includeCompanies}
                onChange={(e) => setIncludeCompanies(e.target.checked)}
              />
              <span>Include companies</span>
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid={TID.cssIncludeSolo}
                checked={includeSolo}
                onChange={(e) => setIncludeSolo(e.target.checked)}
              />
              <span>Include independent contractors</span>
            </label>
          </div>

          {/* Keyword — intentionally de-emphasized */}
          <details className="mt-4">
            <summary className="text-xs text-[var(--avcp-ink-soft)] cursor-pointer select-none">
              Search by company or contractor name
            </summary>
            <input
              type="text"
              className="avcp-input mt-2 max-w-md"
              placeholder="e.g. ACME Integration"
              data-testid={TID.cssKeyword}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </details>

          <div className="mt-5">
            <button
              type="button"
              className="avcp-btn primary"
              data-testid={TID.cssSubmit}
              disabled={busy}
              onClick={() => submit()}
            >
              {busy ? "Searching…" : "Find matching contractors"}
            </button>
          </div>
        </section>

        {/* Results */}
        {searched && (
          <section className="space-y-4">
            <header className="flex flex-wrap gap-3 items-baseline justify-between">
              <h2 className="text-lg font-semibold">
                {results.length === 0
                  ? "No matches yet"
                  : `${results.length} of ${total} matches`}
              </h2>
              {results.length > 0 && (
                <label className="flex items-center gap-2 text-xs">Sort
                  <select aria-label="Sort estimated quotes" className="rounded-lg border px-2 py-1 bg-white" value={priceOrder} onChange={e=>setPriceOrder(e.target.value)}><option value="default">Preferred first</option><option value="match">Best crew match</option><option value="low" disabled={!planning}>Quote: Low to high</option><option value="high" disabled={!planning}>Quote: High to low</option></select>
                </label>
              )}
            </header>

            {results.length === 0 && (
              <div className="avcp-card p-6 text-sm text-[var(--avcp-ink-soft)]"
                   data-testid={TID.cssResultsEmpty}>
                No company or contractor matches these crew requirements yet.
                Try relaxing the filters, removing the Verified-only toggle, or
                broadening the qty.
              </div>
            )}

            {(priceOrder==='default' ? preferredFirst(preferred?.items || [], results, card => ({ owner_kind: card.owner_kind, owner_id: card.owner_id })) : results).map((card, idx) => (
              <ResultCard
                key={`${card.owner_kind}-${card.owner_id}`}
                card={card}
                idx={idx}
                marketplace={marketplace}
                jobId={jobId}
                planning={planning}
                requestRequirements={applied?.requirements || []}
                canRequest={user?.roles?.includes("client")}
              />
            ))}
            {nextOffset != null && <button className="avcp-btn ghost" disabled={busy} onClick={() => submit(true)}>{busy ? "Loading…" : "Show more contractors"}</button>}
          </section>
        )}

      </div>
    </Shell>
  );
}

function ResultCard({ card, idx, marketplace, jobId, canRequest, requestRequirements, planning }) {
  const ev = EVIDENCE_PILL[card.evidence_level] || EVIDENCE_PILL.self;
  return (
    <article
      className="avcp-card p-5"
      data-testid={`${TID.cssResultCard}-${idx}`}
    >
      {planning&&<p className="text-sm mb-3" data-testid="company-quote-preview"><strong>{card.project_estimate?.travel_pending?'Labour estimate (travel pending)':'Estimated quote'}: {card.project_estimate ? money(card.project_estimate.total,card.project_estimate.currency) : 'Needs confirmation'}</strong>{card.project_estimate&&<> · {card.project_estimate.crew_size} {card.project_estimate.crew_size===1?'person':'people'} × {card.project_estimate.billable_hours??card.project_estimate.hours} billable h{Number(planning.budget)>0&&<> · {card.project_estimate.total<=planning.budget?(card.project_estimate.travel_pending?'Labour within budget; travel pending':'Within budget'):`${money(card.project_estimate.total-planning.budget,planning.currency)} over budget`}</>}</>}{!card.project_estimate&&' · Missing crew roles or rates in the project currency'}</p>}
      <WorkTermsSummary terms={card.work_terms} currency={planning?.currency}/>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="text-lg font-semibold">
            {card.display_name}
            {card.legal_name && card.legal_name !== card.display_name && (
              <span className="text-sm font-normal text-[var(--avcp-ink-soft)] ml-2">
                {card.legal_name}
              </span>
            )}
          </h3>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <Pill tone={card.owner_kind === "org" ? "company" : "self"}>
              {card.owner_kind === "org" ? "Company" : "Independent contractor"}
            </Pill>
            {card.employee_count_band && (
              <Pill tone="neutral">{card.employee_count_band}</Pill>
            )}
            <Pill tone={ev.tone} testid={`${TID.cssResultEvidence}-${idx}`}>
              {ev.label}
            </Pill>
            {card.fully_covers ? (
              <Pill tone="ok">Fully covers crew</Pill>
            ) : (
              <Pill tone="warn">
                Partial · {card.matched_count}/{card.total_requirements}
              </Pill>
            )}
            {card.track_match && card.primary_track_codes?.length > 0 && (
              <Pill tone="info">
                Track: {card.primary_track_codes.join(", ")}
              </Pill>
            )}
            {card.insurance_status && card.insurance_status !== "none" && (
              <Pill tone="ok">Insurance: {card.insurance_status}</Pill>
            )}
          </div>
          {card.coverage_area_summary && (
            <p className="text-xs text-[var(--avcp-ink-soft)] mt-2">
              Coverage: {card.coverage_area_summary}
            </p>
          )}
        </div>
        <div className="text-right">
          <div className="text-xs text-[var(--avcp-ink-soft)]">Crew hourly total</div>
          <div className="text-xl font-semibold tabular-nums">
            {card.estimated_hourly_total != null
              ? `$${card.estimated_hourly_total.toFixed(2)}/hr`
              : "—"}
          </div>
          <div className="text-xs text-[var(--avcp-ink-soft)]">
            Matches {card.matched_count} of {card.total_requirements} requested roles
          </div>
          {canRequest && (card.owner_kind === 'org' || card.user_uuid) && <Link
            className="avcp-btn primary mt-3" data-testid={`${TID.cssResultSendRfq}-${idx}`}
            to={jobRequestUrl(card, jobId)} state={{ crewRequirements: requestRequirements, planning }}>Send job request</Link>}
        </div>
      </div>

      {(card.owner_kind === 'org' || marketplace === 'integrator') && <div className="mb-4"><PreferredButton target={{ owner_kind: card.owner_kind, owner_id: card.owner_id }} /></div>}
      <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">Listed crew capacity. Availability for your dates must be confirmed.</p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm" data-testid={`${TID.cssResultMatched}-${idx}`}>
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[var(--avcp-ink-soft)] border-b border-[var(--avcp-line)]">
              <th className="py-2 pr-3">Requirement</th>
              <th className="py-2 pr-3">Role</th>
              <th className="py-2 pr-3">Need</th>
              <th className="py-2 pr-3">Offered</th>
              <th className="py-2 pr-3">Hourly rate</th>
              <th className="py-2 pr-3">Site lead</th>
            </tr>
          </thead>
          <tbody>
            {card.matched_rows.map((r) => (
              <tr key={r.requirement_index} className="border-b border-[var(--avcp-line)]">
                <td className="py-2 pr-3 text-[var(--avcp-ink-soft)]">#{r.requirement_index + 1}</td>
                <td className="py-2 pr-3 font-medium">{r.title_label}</td>
                <td className="py-2 pr-3 tabular-nums">{r.quantity_required}</td>
                <td className="py-2 pr-3 tabular-nums">{r.quantity_available}</td>
                <td className="py-2 pr-3 tabular-nums">
                  {r.currency} {Number(r.hourly_rate).toFixed(2)}
                </td>
                <td className="py-2 pr-3">
                  {r.site_lead_capable ? (
                    <Pill tone="ok">site lead</Pill>
                  ) : r.site_lead_required ? (
                    <Pill tone="danger">required, none</Pill>
                  ) : (
                    <span className="text-xs text-[var(--avcp-ink-soft)]">—</span>
                  )}
                </td>
              </tr>
            ))}
            {card.unmatched_requirements?.length > 0 &&
              card.unmatched_requirements.map((r) => (
                <tr key={`u-${r.requirement_index}`}
                    className="border-b border-[var(--avcp-line)] bg-rose-50/40"
                    data-testid={`${TID.cssResultUnmatched}-${idx}-${r.requirement_index}`}>
                  <td className="py-2 pr-3 text-[var(--avcp-ink-soft)]">#{r.requirement_index + 1}</td>
                  <td className="py-2 pr-3 font-medium">{r.title_code}</td>
                  <td className="py-2 pr-3 tabular-nums">{r.quantity_required}</td>
                  <td className="py-2 pr-3 tabular-nums text-rose-600">no match</td>
                  <td className="py-2 pr-3">—</td>
                  <td className="py-2 pr-3">
                    {r.site_lead_required && <Pill tone="danger">site lead needed</Pill>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <ContractorCalendar target={{owner_kind:card.owner_kind,owner_id:card.owner_id,...(card.user_uuid?{user_uuid:card.user_uuid}:{})}} name={card.display_name}/>
    </article>
  );
}
